import type { TrailerBodyStyle, TruckBodyStyle, TyreLayoutAxleRole, TyreLayoutSubjectKind } from '@chukua/common';

export type VehicleBodyStyle = TruckBodyStyle | TrailerBodyStyle;

export const TRUCK_BODY_STYLE_LABELS: Record<TruckBodyStyle, string> = {
  tractor: 'Prime mover',
  tipper: 'Tipper',
  box: 'Box body',
  tanker: 'Tanker',
  dropside: 'Dropside',
};

export const TRAILER_BODY_STYLE_LABELS: Record<TrailerBodyStyle, string> = {
  skeletal_40: 'Skeletal, 40ft',
  skeletal_20: 'Skeletal, 20ft',
  flatbed: 'Flatbed',
  sidewall: 'Side-wall',
  tanker: 'Fuel tanker',
  lowbed: 'Lowbed',
  reefer: 'Reefer box',
  tipper: 'Tipper',
  drawbar: 'Drawbar',
};

export const AXLE_ROLE_LABELS: Record<TyreLayoutAxleRole, string> = {
  steer: 'Steer',
  drive: 'Drive',
  tag: 'Tag',
  rear: 'Rear',
  turntable: 'Turntable',
};

/**
 * A truck body's proportions in metres: the wheelbase from the last steer
 * axle to the first rear axle (a prime mover's is shorter over a tandem), the
 * overhang behind the last axle, and the height the camera looks at.
 */
export interface TruckStyleDimensions {
  wheelbaseSingle: number;
  wheelbaseTandem: number;
  overhang: number;
  lookAtHeight: number;
}

export const TRUCK_STYLE_DIMENSIONS: Record<TruckBodyStyle, TruckStyleDimensions> = {
  tractor: { wheelbaseSingle: 3.6, wheelbaseTandem: 3.3, overhang: 1.0, lookAtHeight: 1.4 },
  tipper: { wheelbaseSingle: 3.4, wheelbaseTandem: 3.1, overhang: 1.05, lookAtHeight: 1.5 },
  box: { wheelbaseSingle: 4.3, wheelbaseTandem: 4.3, overhang: 1.7, lookAtHeight: 1.8 },
  tanker: { wheelbaseSingle: 3.9, wheelbaseTandem: 3.7, overhang: 1.2, lookAtHeight: 1.6 },
  dropside: { wheelbaseSingle: 4.4, wheelbaseTandem: 4.4, overhang: 1.4, lookAtHeight: 1.4 },
};

/**
 * A trailer body's length in metres and what it rides on: a lowbed's deck
 * sits over 17.5" wheels, so its axles hang lower and carry no mudguards.
 */
export interface TrailerStyleDimensions {
  length: number;
  lookAtHeight: number;
  hasSmallWheels?: boolean;
  carriesContainer?: boolean;
}

export const TRAILER_STYLE_DIMENSIONS: Record<TrailerBodyStyle, TrailerStyleDimensions> = {
  skeletal_40: { length: 12.31, lookAtHeight: 1.3, carriesContainer: true },
  skeletal_20: { length: 6.18, lookAtHeight: 1.3, carriesContainer: true },
  flatbed: { length: 12.4, lookAtHeight: 1.3, carriesContainer: true },
  sidewall: { length: 12.4, lookAtHeight: 1.5 },
  tanker: { length: 11.4, lookAtHeight: 1.8 },
  lowbed: { length: 13.2, lookAtHeight: 1.0, hasSmallWheels: true },
  reefer: { length: 13.6, lookAtHeight: 2.0 },
  tipper: { length: 9.6, lookAtHeight: 1.6 },
  drawbar: { length: 8.2, lookAtHeight: 1.3, carriesContainer: true },
};

const DEFAULT_STYLE: Record<TyreLayoutSubjectKind, VehicleBodyStyle> = { truck_class: 'tractor', trailer_type: 'skeletal_40' };

export function isTruckBodyStyle(style: string): style is TruckBodyStyle {
  return style in TRUCK_BODY_STYLE_LABELS;
}

export function isTrailerBodyStyle(style: string): style is TrailerBodyStyle {
  return style in TRAILER_BODY_STYLE_LABELS;
}

/** The style to draw: the subject's own, or its kind's default when it names one the other kind has (or none). */
export function bodyStyleFor(kind: TyreLayoutSubjectKind, style: string | null | undefined): VehicleBodyStyle {
  if (!style) return DEFAULT_STYLE[kind];
  const fits = kind === 'truck_class' ? isTruckBodyStyle(style) : isTrailerBodyStyle(style);
  return fits ? (style as VehicleBodyStyle) : DEFAULT_STYLE[kind];
}

export function bodyStyleLabel(style: string): string {
  if (isTruckBodyStyle(style)) return TRUCK_BODY_STYLE_LABELS[style];
  if (isTrailerBodyStyle(style)) return TRAILER_BODY_STYLE_LABELS[style];
  return style;
}

export function canCarryContainer(style: VehicleBodyStyle): boolean {
  return isTrailerBodyStyle(style) && TRAILER_STYLE_DIMENSIONS[style].carriesContainer === true;
}
